import { FlatfileRecord } from '@flatfile/plugin-record-hook';
import type { VisitorManagementFeatureProperties } from 'application-types';
import { isRequired } from 'lane-shared/domains/visitorManagement/features/VisitorManagement';
import type { FormCustomFieldColumn } from './workbook';

import { validateCustomFieldValue } from '../validators/validateCustomFieldValue';

export const validateRecord = (
  record: FlatfileRecord,
  props: VisitorManagementFeatureProperties,
  formCustomFields: FormCustomFieldColumn[] = [],
  /** When true, runs format/length/option rules via validateCustomFieldValue (gated by VM custom-fields flag at call site). */
  enableCustomFieldValueValidation = false
) => {
  const email = record.get('email');
  const countryCode = record.get('countryCode');
  const cellNumber = record.get('cellNumber');
  const company = record.get('company');

  // Validate required fields
  if (isRequired(props.emailSetting) && !email) {
    record.addError('email', 'Email is required');
  }

  if (isRequired(props.phoneSetting) && !cellNumber) {
    record.addError('cellNumber', 'Phone is required');
  }

  if (isRequired(props.companySetting) && !company) {
    record.addError('company', 'Company is required');
  }

  // Validate email format
  const emailRegex = /^(?!.*\.{2})[\p{L}\p{M}\p{N}._%+-]+@[\p{L}\p{M}\p{N}.-]+\.[\p{L}\p{M}]{2,63}$/u;

  if (email && !emailRegex.test(email as string)) {
    record.addError('email', 'is not formatted like an email');
  }

  // Validate phone number format
  const fourteenDigitNumberRegex = /^[1-9]{1}[0-9]{9,13}$/;

  if (cellNumber && !fourteenDigitNumberRegex.test(cellNumber as string)) {
    record.addError(
      'cellNumber',
      'Cell number can not have special characters, spaces, or be less than 10 digits or more than 14 digits'
    );
  }

  // Validate country code format
  const threeDigitNumberRegex = /^[1-9]{1}[0-9]{0,2}$/;

  if (countryCode && !threeDigitNumberRegex.test(countryCode as string)) {
    record.addError(
      'countryCode',
      'Country code can not start with 0, be more than 3 digits, or have special characters'
    );
  }

  // Ensure country code and phone number are both present or both absent
  if ((!countryCode && cellNumber) || (countryCode && !cellNumber)) {
    record.addError(
      ['countryCode', 'cellNumber'],
      'Either both Country Code and Cell Number must be present or both must be absent'
    );
  }

  if (enableCustomFieldValueValidation) {
    // Form custom fields: truly empty cells are skipped here because Flatfile's required
    // column constraint handles them (avoids duplicate messages). Whitespace-only values
    // are NOT skipped — they must be validated so the required error surfaces.
    for (const field of formCustomFields) {
      const rawValue = record.get(field.fieldKey);
      const isTrulyEmpty =
        rawValue === undefined || rawValue === null || String(rawValue) === '';

      if (isTrulyEmpty) {
        continue;
      }

      const value = String(rawValue).trim();

      const error = validateCustomFieldValue(
        {
          fieldKey: field.fieldKey,
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          options: field.options,
        },
        value
      );

      if (error) {
        record.addError(field.fieldKey, error);
      }
    }
  }

  return record;
};
